import Router from "vue-router";
import store from "@Store";
import routes from "./routes";
import { useNavbarStore } from "@Resources/js/modules/shared/useNavbarStore";
import { XShoppingCartStore } from "@Modules/shoppingcart/Store/XShoppingCartStore";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
import { XTeamsStore } from "@Modules/teams/Store/XTeamsStore";

function middlewarePipeline(context, middleware, index) {
    const nextMiddleware = middleware[index];

    if (nextMiddleware) {
        return () => {
            nextMiddleware({
                ...context,
                nextMiddleware: middlewarePipeline(context, middleware, index + 1),
            });
        };
    }

    return context.next;
}

/**
 * It resets the keyboard navigation to start in the application navbar.
 */
function resetKeyboardNavigation() {
    const navbar = document.getElementById("application-navbar");

    if (navbar === null) {
        return;
    }

    navbar.focus();
}

const router = new Router({
    mode: "history",
    routes,
    /**
     * FIXME: Probably is not going to work with legacy experiences.
     * We have to carefully review legacy experiences to avoid, if possible,
     * scroll to top when route changed.
     */
    scrollBehavior(to, from, savedPosition) {
        const navigationHeight = document.querySelector("#application-navbar")?.offsetHeight;

        if (to.hash && navigationHeight !== undefined) {
            const EXTRA_OFFSET = 32;

            return {
                selector: to.hash,
                behavior: "smooth",
                offset: {
                    x: 0,
                    y: navigationHeight + EXTRA_OFFSET,
                },
            };
        } else {
            return {
                x: 0,
                y: 0,
            };
        }
    },
});

// We add the router to the window so that we can access it using the visit methods that are curried into
// server resources.
window.$router = router;

// Initialize shopping cart after navigation
router.afterEach((to, from) => {
    const toOrgSlug = to.params?.org;
    const fromOrgSlug = from.params?.org;
    const isLoggedIn = XAuthStore().isLoggedIn;
    const cartStore = XShoppingCartStore();

    // Skip if not logged in
    if (!isLoggedIn) {
        return;
    }

    // Only handle if context changed or cart not initialized
    if (cartStore.initialized && toOrgSlug === fromOrgSlug) {
        return;
    }

    if (toOrgSlug) {
        const organization = XTeamsStore().getOrganizationBySlug(toOrgSlug);
        if (organization) {
            console.log("[Router] Initializing team cart for:", toOrgSlug);
            cartStore.initializeTeamCart(organization);
        }
    } else {
        console.log("[Router] Initializing user cart");
        cartStore.initializeCart();
    }
});

router.beforeEach((to, from, next) => {
    // Check if the navbar should be shown on this route
    let shouldShowNavbar = to?.meta?.navbarEnabled;
    if (shouldShowNavbar === undefined) {
        shouldShowNavbar = true;
    }
    useNavbarStore().enableNavbar(shouldShowNavbar);

    console.log("vue router is navigating to", { name: to.name, path: to.path, params: { ...to.params }, query: { ...to.query } });

    if (to.query && to.query.coupon && to.query.coupon.length > 0) {
        store.dispatch("rememberCoupon", to.query.coupon);
    }

    store.dispatch("waitForSparkState").then(() => {
        const middleware = (to.meta && to.meta.middleware) || [];
        if (middleware.length === 0) return next();

        const context = {
            to,
            from,
            next,
            store,
        };

        middleware[0]({
            ...context,
            nextMiddleware: middlewarePipeline(context, middleware, 1),
        });
    });
});

router.afterEach(() => {
    resetKeyboardNavigation();
});

// disables vue-router navigation duplicated error
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location, onResolve, onReject) {
    if (onResolve || onReject) {
        return originalPush.call(this, location, onResolve, onReject);
    }

    return originalPush.call(this, location).catch((err) => {
        if (Router.isNavigationFailure(err)) return err;
        return Promise.reject(err);
    });
};

export default router;

const START_LOCATION = Router.START_LOCATION;
export { START_LOCATION };
